// src/pages/admin/Dashboard.jsx - COMPLETE MOBILE RESPONSIVE FIXED
import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  People,
  Store,
  ShoppingCart,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  VerifiedUser,
  Warning,
  CheckCircle,
  Inventory,
  LocalShipping,
  Pending,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#d32f2f', '#f44336', '#ff9800', '#4caf50', '#2196f3'];

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [orderStatus, setOrderStatus] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getDashboardStats();
      const data = response.data;
      
      setStats(data.stats);
      setRecentOrders(data.recentOrders || []);
      setOrderStatus(data.orderStatus || []);
      setMonthlyRevenue(data.monthlyRevenue || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const metrics = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: <People />,
      color: '#2196f3',
      bgColor: '#2196f320',
    },
    {
      title: 'Total Vendors',
      value: stats?.totalVendors || 0,
      icon: <Store />,
      color: '#4caf50',
      bgColor: '#4caf5020',
    },
    {
      title: 'Total Products',
      value: stats?.totalProducts || 0,
      icon: <Inventory />,
      color: '#ff9800',
      bgColor: '#ff980020',
    },
    {
      title: 'Total Orders',
      value: stats?.totalOrders || 0,
      icon: <ShoppingCart />,
      color: '#d32f2f',
      bgColor: '#d32f2f20',
    },
    {
      title: 'Total Revenue',
      value: `$${stats?.totalRevenue?.toFixed(2) || '0.00'}`,
      icon: <AttachMoney />,
      color: '#9c27b0',
      bgColor: '#9c27b020',
    },
  ];

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3, 
          borderRadius: 3, 
          bgcolor: 'primary.main', 
          color: 'white' 
        }}
      >
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
          Admin Dashboard
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Overview of the entire platform
        </Typography>
      </Paper>

      {/* Stats Cards - Responsive Grid */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        {metrics.map((metric) => (
          <Grid item xs={6} sm={6} md={4} lg={2.4} key={metric.title}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack 
                  direction="row" 
                  alignItems="center" 
                  justifyContent="space-between" 
                  sx={{ mb: 1 }}
                >
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    fontWeight="600"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' } }}
                  >
                    {metric.title}
                  </Typography>
                  <Box
                    sx={{
                      p: { xs: 0.5, sm: 1 },
                      borderRadius: 1,
                      bgcolor: metric.bgColor,
                      color: metric.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {metric.icon}
                  </Box>
                </Stack>
                <Typography 
                  variant={isMobile ? "h6" : "h4"} 
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2rem' } }}
                >
                  {metric.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section - Responsive */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: 3 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold" sx={{ mb: 2 }}>
              Monthly Revenue
            </Typography>
            <Box sx={{ height: isMobile ? 200 : 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#d32f2f"
                    strokeWidth={2}
                    name="Revenue ($)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: 3, height: '100%' }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold" sx={{ mb: 2 }}>
              Order Status
            </Typography>
            <Box sx={{ height: isMobile ? 180 : 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={orderStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={isMobile ? 40 : 60}
                    outerRadius={isMobile ? 60 : 80}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="status"
                  >
                    {orderStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Grid container spacing={0.5} sx={{ mt: 1 }}>
              {orderStatus.map((entry, index) => (
                <Grid item xs={6} key={entry.status}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: COLORS[index % COLORS.length],
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                      {entry.status}: {entry.count}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Orders - Responsive */}
      <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between" 
          sx={{ mb: 2 }}
        >
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
            Recent Orders
          </Typography>
          <Button 
            size="small" 
            onClick={() => window.location.href = '/admin/orders'}
            variant="outlined"
            sx={{ mt: { xs: 1, sm: 0 } }}
          >
            View All
          </Button>
        </Stack>
        
        {isMobile ? (
          // Mobile Card View for Recent Orders
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {recentOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary">No recent orders</Typography>
              </Box>
            ) : (
              recentOrders.slice(0, 5).map((order) => (
                <Card key={order.id} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack spacing={0.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" fontWeight="bold">
                          #{order.orderNumber || order.id}
                        </Typography>
                        <Chip
                          label={order.status || 'Pending'}
                          size="small"
                          color={
                            order.status === 'delivered' ? 'success' : 
                            order.status === 'cancelled' ? 'error' : 
                            order.status === 'shipped' ? 'primary' : 
                            order.status === 'processing' ? 'info' : 'warning'
                          }
                          sx={{ height: 20, fontSize: '0.6rem' }}
                        />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          {order.User?.name || 'Unknown'}
                        </Typography>
                        <Typography variant="body2" fontWeight="600" color="primary">
                          ${order.totalAmount?.toFixed(2) || '0.00'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Order ID</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Customer</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Vendor</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Total</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography color="text.secondary">No recent orders</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentOrders.slice(0, 10).map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          #{order.orderNumber || order.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.User?.name || 'Unknown'}</TableCell>
                      <TableCell>{order.Vendor?.storeName || 'Unknown'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" color="primary">
                          ${order.totalAmount?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status || 'Pending'}
                          size="small"
                          color={
                            order.status === 'delivered' ? 'success' : 
                            order.status === 'cancelled' ? 'error' : 
                            order.status === 'shipped' ? 'primary' : 
                            order.status === 'processing' ? 'info' : 'warning'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AdminDashboard;